import React, { Fragment, useRef, useContext } from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SearchIcon,
    PlusIcon,
    UsersIcon,
    EyeIcon,
    EyeOffIcon,
    TrashIcon,
    SupportIcon,
} from "@heroicons/react/outline";
import { Link, useHistory, useLocation } from "react-router-dom";
import { AuthorListContext } from ".";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { apiPut, apiDel, apiPost, messageFromException } from "helpers/Request";
import { useContextualHelp } from "hooks/Help";
import { modal } from "components/HeadlessModal";
import PrimaryButton from "components/Buttons/PrimaryButton";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function AuthorListToolbar(props) {
    const inputRef = useRef();
    const textInputRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const auth = useAuth();
    const contextualHelp = useContextualHelp();

    const { selectedItems, load } = useContext(AuthorListContext);

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && val === null) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Authors",
            children: (
                <Fragment>
                    <div className="mb-4">
                        <strong>
                            It is strongly recommended that you disable authors
                            instead of deleting them.
                        </strong>{" "}
                        Deleting an author will disassociate the author from any
                        content, breaking their author pages. Disabling the
                        author will prevent them from showing in author lists.
                    </div>
                    <div>Are you sure you want to delete these authors?</div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={handleDelete}>
                        Delete Author
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        modal.update({
            disabled: true,
        });
        const id = toast.loading("Deleting authors...");

        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/author/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Authors deleted", { id });
                modal.dismiss();
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete authors",
                    children: messageFromException(err),
                });
            });
    };

    const onRequestUpdate = (data) => {
        const id = toast.loading("Updating authors...");

        const promises = selectedItems.map((uuid) =>
            apiPut(`/v4/author/${uuid}`, auth.publication, data)
        );
        Promise.all(promises)
            .then(() => {
                toast.success("Authors updated", { id });
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to update authors",
                    children: messageFromException(err),
                });
            });
    };

    const onImport = () => {
        modal({
            title: "Import Authors",
            children: (
                <Fragment>
                    <div className="mb-4">
                        Add authors, either one per line, or comma separated.
                    </div>
                    <div>
                        <textarea
                            ref={textInputRef}
                            rows={5}
                            className="block w-full rounded-md border-gray-300 py-2 px-4 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-white sm:text-sm"
                            placeholder="Mayme McDonald, Dennis Fitzgerald, Minnie Bishop, ..."
                        />
                    </div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <PrimaryButton onClick={handleImport}>
                        Import Authors
                    </PrimaryButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleImport = async () => {
        modal.update({ disabled: true });
        const id = toast.loading("Importing authors...");

        const data = textInputRef.current.value
            .split(/\n|\r|\r\n|,/)
            .map((name) => name.trim())
            .filter((name) => name.length > 0);

        const promises = data.map((name) =>
            apiPost("/v4/author", auth.publication, {
                name,
                slug: name.toLowerCase().replace(/\s+/g, "-"),
                active: true,
            })
        );

        return Promise.all(promises)
            .then(() => {
                toast.success("Authors added", { id });
                modal.dismiss();
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to add authors",
                    children: messageFromException(err),
                });
            });
    };

    const onKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        onSearchClick();
    };
    const onSearchClick = () => {
        const val = inputRef.current.value;
        if (!val || !val.length) {
            updateParams("keyword", null);
        } else {
            updateParams("keyword", val);
        }
    };

    return (
        <Toolbar>
            <ToolbarGroup>
                <input
                    type="text"
                    ref={inputRef}
                    className="focus:outline-none relative mr-2 flex items-center rounded-md border border-gray-300 bg-white py-0 px-2 text-xs placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500"
                    placeholder="Search Authors"
                    onKeyDown={onKeyDown}
                />
                <ToolbarButton icon={<SearchIcon />} onClick={onSearchClick} />
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<PlusIcon />}
                    as={Link}
                    to="/app/author/new"
                >
                    <span className="hidden sm:inline">Add</span>
                </ToolbarButton>
                <ToolbarButton icon={<UsersIcon />} onClick={onImport}>
                    Import Authors
                </ToolbarButton>
                <ToolbarButton
                    icon={<EyeIcon />}
                    disabled={!selectedItems.length}
                    onClick={() => onRequestUpdate({ status: 1 })}
                >
                    Activate
                </ToolbarButton>
                <ToolbarButton
                    icon={<EyeOffIcon />}
                    disabled={!selectedItems.length}
                    onClick={() => onRequestUpdate({ status: 0 })}
                >
                    Deactivate
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={onRequestDelete}
                    disabled={!selectedItems.length}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    className="text-green-600"
                    onClick={() =>
                        contextualHelp.showContextualHelp("/author/index.html")
                    }
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
